import type { ISbStoryData } from "@storyblok/react";
import { useTranslations } from "next-intl";

import type { RichTextBlok } from "@/bloks/RichText/RichText";
import type { TaggedImageBlok } from "@/bloks/TaggedImage/TaggedImage";
import { useProductFilter } from "@/lib/centra/useProductFilter/useProductFilter";
import type { Subcategory } from "@/pages/api/storefront/subcategories";
import type { ICentraFilter, IFilterGroup } from "@/types/centra";

import FilterBar from "../FilterBar";
import PageIntro from "../PageIntro";
import ProductGrid from "../Product/ProductGrid";

const sizesCallback = (values: ICentraFilter[]): ICentraFilter[] => {
  const sortOrder: string[] = [
    "XXXS",
    "XXS",
    "XS",
    "S",
    "M",
    "L",
    "XL",
    "XXL",
    "XXXL",
  ];

  values.sort((a, b) => {
    if (sortOrder.indexOf(a.value) < sortOrder.indexOf(b.value)) {
      return -1;
    }
    if (sortOrder.indexOf(a.value) > sortOrder.indexOf(b.value)) {
      return 1;
    }

    return 0;
  });

  return values;
};

const sortByFilterCountDesc = (values: ICentraFilter[]): ICentraFilter[] => {
  values.sort((a, b) => {
    if (a.filterCount === b.filterCount) return 0;

    return b.filterCount - a.filterCount;
  });

  return values;
};

const getFilters = (t: (key: string) => string) => {
  const filter: IFilterGroup[] = [
    {
      name: t("colors"),
      field: "pr_colors.name",
      callback: sortByFilterCountDesc,
    },
    {
      name: t("size-0"),
      field: "items.name",
      callback: sizesCallback,
    },
    {
      name: t("designer"),
      field: "pr_designer.name",
      callback: sortByFilterCountDesc,
    },
    {
      name: t("type"),
      field: "pr_composition",
      callback: sortByFilterCountDesc,
    },
    // {
    //   name: "collection",
    //   field: "collections",
    //   callback: sortByFilterCountDesc,
    //   labelField: "name",
    // },
  ];

  return filter;
};

export interface CategoryBlok extends RichTextBlok {
  featuredProducts?: TaggedImageBlok[];
  metadata?: {
    title?: string;
    description?: string;
    og_title?: string;
    og_description?: string;
    og_image?: string;
  };
}
const PLP = ({
  story,
  subcategories,
  categoryIds,
}: {
  story?: ISbStoryData<CategoryBlok>;
  subcategories: Subcategory[];
  categoryIds?: string[];
}) => {
  const t = useTranslations();
  const {
    products,
    sortOrder,
    filterObjects,
    productCount,
    clearFilters,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProductFilter(categoryIds, getFilters(t));

  const loadMore = hasNextPage
    ? { callback: fetchNextPage, loading: isFetchingNextPage }
    : { callback: () => {}, loading: false };

  return (
    <>
      <div>
        <PageIntro
          content={story?.content}
          initialSubcategories={subcategories}
        />
        <FilterBar
          sortOrder={sortOrder}
          filters={filterObjects}
          productCount={productCount || 0}
          clearFilters={clearFilters}
        />
        <ProductGrid
          products={products}
          featured={story?.content?.featuredProducts}
          loadMore={loadMore}
        />
      </div>
    </>
  );
};

export default PLP;
